import { UserPermissions } from "@/components/UserPermissions";
import { useEffect, useState } from "react";
import {
  getTeams, createTeam, updateTeam, deleteTeam, revokeTeamAccess,
  getUsers, createUser, updateUser, deactivateUser,
  getPipelineStatuses, getCallOutcomes,
  createPipelineStatus, updatePipelineStatus, deletePipelineStatus,
  createCallOutcome, updateCallOutcome, deleteCallOutcome,
  getBranding, updateBranding,
  getServices, createService, updateService, deleteService,
  getKbArticles, createKbArticle, updateKbArticle, deleteKbArticle,
  getRinkelUsers,
} from "@/lib/apiClient";
import { RinkelIdField } from "@/components/RinkelIdField";
import { Plus, Trash2, Save, UserPlus, Users2, ShieldOff, Power, Image as ImageIcon, Upload, Package, BookOpen, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { id: "admin", label: "Administrator" },
  { id: "teamleider", label: "Teamleider" },
  { id: "medewerker", label: "Medewerker" },
  { id: "makelaar", label: "Makelaar / Adviseur" },
];
const roleLabel = (r) => (ROLES.find((x) => x.id === r) || {}).label || r;

export function TeamUserEditor() {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [newTeam, setNewTeam] = useState({ name: "", kind: "intern" });
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "medewerker", team_id: "", rinkel_user_id: "" });
  const [rinkelUsers, setRinkelUsers] = useState([]);
  useEffect(() => { getRinkelUsers().then(setRinkelUsers).catch(() => setRinkelUsers([])); }, []);

  const loadAll = () => {
    getTeams().then(setTeams).catch(() => {});
    getUsers().then(setUsers).catch(() => {});
  };
  useEffect(loadAll, []);

  const addTeam = async () => {
    if (!newTeam.name.trim()) return toast.error("Teamnaam is verplicht");
    await createTeam(newTeam);
    setNewTeam({ name: "", kind: "intern" });
    loadAll(); toast.success("Team toegevoegd");
  };
  const saveTeam = async (t) => { await updateTeam(t.id, { name: t.name, kind: t.kind, active: t.active }); toast.success("Team opgeslagen"); loadAll(); };
  const removeTeam = async (t) => {
    if (!window.confirm(`Team "${t.name}" verwijderen?`)) return;
    const res = await deleteTeam(t.id);
    toast.success(res.deactivated ? "Team gedeactiveerd (heeft nog leden)" : "Team verwijderd");
    loadAll();
  };
  const revoke = async (t) => {
    if (!window.confirm(`Toegang van heel "${t.name}" direct intrekken? Alle medewerkers van dit bureau worden gedeactiveerd. Historische data blijft behouden.`)) return;
    const res = await revokeTeamAccess(t.id);
    toast.success(`Toegang ingetrokken · ${res.users_disabled} medewerker(s) gedeactiveerd`);
    loadAll();
  };

  const addUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim()) return toast.error("Naam en e-mail zijn verplicht");
    try {
      const res = await createUser({ ...newUser, team_id: newUser.team_id || null });
      if (res.temp_password) toast.success(`Gebruiker aangemaakt. Tijdelijk wachtwoord: ${res.temp_password}`, { duration: 10000 });
      else toast.success("Gebruiker aangemaakt");
      setNewUser({ name: "", email: "", password: "", role: "medewerker", team_id: "", rinkel_user_id: "" });
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aanmaken mislukt"); }
  };
  const saveUser = async (u) => {
    try {
      await updateUser(u.id, { name: u.name, role: u.role, team_id: u.team_id || null, rinkel_user_id: u.rinkel_user_id || null, is_active: u.is_active, is_advisor: !!u.is_advisor });
      const r = rinkelUsers.find((x) => x.id === u.rinkel_user_id);
      toast.success(r ? `Opgeslagen — gekoppeld aan Rinkel-account ${r.name}` : "Gebruiker opgeslagen"); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };
  const toggleActive = async (u) => { await updateUser(u.id, { is_active: !u.is_active }); toast.success(u.is_active ? "Gedeactiveerd" : "Geactiveerd"); loadAll(); };

  const teamName = (id) => (teams.find((t) => t.id === id) || {}).name || "—";

  return (
    <div className="space-y-10">
      {/* Teams */}
      <section>
        <h2 className="font-heading font-semibold text-xl text-[#16202B] mb-1 flex items-center gap-2"><Users2 className="h-5 w-5 text-[#16BFBF]" /> Teams & externe bureaus</h2>
        <p className="text-sm text-[#5B6675] mb-4">Maak zelf teams of externe belbureaus aan. Je kunt ze hernoemen, deactiveren of de toegang direct intrekken — zonder dat historische leads of gesprekken verloren gaan.</p>
        <div className="flex flex-wrap items-end gap-3 mb-5 rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4">
          <label className="flex-1 min-w-[200px]"><span className="text-xs text-[#5B6675] block mb-1">Naam</span>
            <input data-testid="new-team-name" value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} className="fe-input" placeholder="Bijv. Extern belbureau" /></label>
          <label><span className="text-xs text-[#5B6675] block mb-1">Type</span>
            <select data-testid="new-team-kind" value={newTeam.kind} onChange={(e) => setNewTeam({ ...newTeam, kind: e.target.value })} className="fe-input"><option value="intern">Intern team</option><option value="extern">Extern bureau</option></select></label>
          <button data-testid="add-team" onClick={addTeam} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]"><Plus className="h-4 w-4" /> Toevoegen</button>
        </div>
        <div className="space-y-2">
          {teams.map((t) => (
            <div key={t.id} data-testid={`team-${t.id}`} className={`rounded-xl border border-[#E2E7EC] p-3 flex flex-wrap items-center gap-3 ${t.active ? "bg-white" : "bg-[#F6F9FB] opacity-70"}`}>
              <input value={t.name} onChange={(e) => setTeams((xs) => xs.map((x) => x.id === t.id ? { ...x, name: e.target.value } : x))} className="fe-input flex-1 min-w-[160px]" />
              <select value={t.kind} onChange={(e) => setTeams((xs) => xs.map((x) => x.id === t.id ? { ...x, kind: e.target.value } : x))} className="fe-input w-40"><option value="intern">Intern team</option><option value="extern">Extern bureau</option></select>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${t.active ? "bg-[#D6F3F3] text-[#0C6E6E]" : "bg-[#F3DEDD] text-[#B0413E]"}`}>{t.active ? "Actief" : "Inactief"}</span>
              <button data-testid={`save-team-${t.id}`} onClick={() => saveTeam(t)} className="p-2 rounded-lg text-[#001C51] hover:bg-[#E3E9F5]" title="Opslaan"><Save className="h-4 w-4" /></button>
              <button data-testid={`revoke-team-${t.id}`} onClick={() => revoke(t)} className="p-2 rounded-lg text-[#B7791F] hover:bg-[#FBF0DC]" title="Toegang intrekken"><ShieldOff className="h-4 w-4" /></button>
              <button data-testid={`del-team-${t.id}`} onClick={() => removeTeam(t)} className="p-2 rounded-lg text-[#B0413E] hover:bg-[#F3DEDD]" title="Verwijderen"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {teams.length === 0 && <p className="text-sm text-[#5B6675] py-3">Nog geen teams. Voeg je eerste team of bureau toe.</p>}
        </div>
      </section>

      {/* Users */}
      <section>
        <h2 className="font-heading font-semibold text-xl text-[#16202B] mb-1 flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#16BFBF]" /> Gebruikers</h2>
        <p className="text-sm text-[#5B6675] mb-4">Koppel elke medewerker aan een team en een rol. Medewerkers zien uitsluitend de leads waarvoor ze toegang hebben.</p>
        <div className="flex flex-wrap items-end gap-3 mb-5 rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4">
          <label className="min-w-[140px] flex-1"><span className="text-xs text-[#5B6675] block mb-1">Naam</span><input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="fe-input" /></label>
          <label className="min-w-[160px] flex-1"><span className="text-xs text-[#5B6675] block mb-1">E-mail</span><input data-testid="new-user-email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="fe-input" /></label>
          <label className="min-w-[120px]"><span className="text-xs text-[#5B6675] block mb-1">Wachtwoord</span><input data-testid="new-user-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="fe-input" placeholder="Auto indien leeg" /></label>
          <label><span className="text-xs text-[#5B6675] block mb-1">Rol</span><select data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="fe-input">{ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></label>
          <label><span className="text-xs text-[#5B6675] block mb-1">Team</span><select data-testid="new-user-team" value={newUser.team_id} onChange={(e) => setNewUser({ ...newUser, team_id: e.target.value })} className="fe-input"><option value="">— Geen —</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
          <label><span className="text-xs text-[#5B6675] block mb-1">Rinkel-account</span><RinkelIdField testid="new-user-rinkel" value={newUser.rinkel_user_id} rinkelUsers={rinkelUsers} onChange={(v) => setNewUser({ ...newUser, rinkel_user_id: v })} /></label>
          <label className="inline-flex items-center gap-1.5 text-xs text-[#16202B] pb-3"><input type="checkbox" data-testid="new-user-advisor" checked={!!newUser.is_advisor} onChange={(e) => setNewUser({ ...newUser, is_advisor: e.target.checked })} className="accent-[#16BFBF] h-4 w-4" /> Adviseur</label>
          <button data-testid="add-user" onClick={addUser} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]"><Plus className="h-4 w-4" /> Toevoegen</button>
        </div>
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-${u.id}`} className={`rounded-xl border border-[#E2E7EC] p-3 flex flex-wrap items-center gap-3 ${u.is_active ? "bg-white" : "bg-[#F6F9FB] opacity-70"}`}>
              <div className="min-w-[180px]">
                <input value={u.name} onChange={(e) => setUsers((xs) => xs.map((x) => x.id === u.id ? { ...x, name: e.target.value } : x))} className="fe-input" />
                <p className="text-xs text-[#5B6675] mt-1">{u.email}</p>
              </div>
              <select value={u.role} onChange={(e) => setUsers((xs) => xs.map((x) => x.id === u.id ? { ...x, role: e.target.value } : x))} className="fe-input w-44">{ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select>
              <select value={u.team_id || ""} onChange={(e) => setUsers((xs) => xs.map((x) => x.id === u.id ? { ...x, team_id: e.target.value } : x))} className="fe-input w-40"><option value="">— Geen team —</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
              <RinkelIdField testid={`rinkel-id-${u.id}`} value={u.rinkel_user_id} rinkelUsers={rinkelUsers} onChange={(v) => setUsers((xs) => xs.map((x) => x.id === u.id ? { ...x, rinkel_user_id: v } : x))} />
              <label className="inline-flex items-center gap-1.5 text-xs text-[#16202B]" title="Adviseur: bij deze gebruiker kunnen afspraken worden geboekt">
                <input type="checkbox" data-testid={`advisor-${u.id}`} checked={!!u.is_advisor} onChange={(e) => setUsers((xs) => xs.map((x) => x.id === u.id ? { ...x, is_advisor: e.target.checked } : x))} className="accent-[#16BFBF] h-4 w-4" /> Adviseur
              </label>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${u.is_active ? "bg-[#D6F3F3] text-[#0C6E6E]" : "bg-[#F3DEDD] text-[#B0413E]"}`}>{u.is_active ? "Actief" : "Inactief"}</span>
              <button data-testid={`save-user-${u.id}`} onClick={() => saveUser(u)} className="p-2 rounded-lg text-[#001C51] hover:bg-[#E3E9F5]" title="Opslaan"><Save className="h-4 w-4" /></button>
              <button data-testid={`toggle-user-${u.id}`} onClick={() => toggleActive(u)} className="p-2 rounded-lg text-[#B7791F] hover:bg-[#FBF0DC]" title={u.is_active ? "Deactiveren" : "Activeren"}><Power className="h-4 w-4" /></button>
              {u.role !== "admin" && <UserPermissions u={u} onSaved={(perms) => setUsers((xs) => xs.map((x) => x.id === u.id ? { ...x, permissions: perms } : x))} />}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function BrandingEditor() {
  const [logo, setLogo] = useState("");
  const [companyName, setCompanyName] = useState("Flex Estate");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getBranding().then((b) => { setLogo(b.logo_data_url || ""); setCompanyName(b.company_name || "Flex Estate"); }).catch(() => {});
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Kies een afbeelding (PNG, JPG of SVG)");
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result || "");
      // Downscale raster images to max 400px to keep storage tiny; keep SVG as-is.
      if (file.type === "image/svg+xml") { setLogo(src); return; }
      const img = new Image();
      img.onload = () => {
        const max = 400;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        setLogo(c.toDataURL("image/png"));
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateBranding({ logo_data_url: logo, company_name: companyName });
      toast.success("Branding opgeslagen. Herlaad de pagina om het overal te zien.");
      window.dispatchEvent(new Event("branding-updated"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="font-heading font-semibold text-xl text-[#16202B] mb-1 flex items-center gap-2"><ImageIcon className="h-5 w-5 text-[#16BFBF]" /> Branding & logo</h2>
      <p className="text-sm text-[#5B6675] mb-6">Upload je Flex Estate-logo. Het verschijnt op de inlogpagina, in de navigatie en op mobiel. Het logo wordt veilig in de database opgeslagen — geen externe opslagkosten.</p>

      <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6 space-y-6">
        <div>
          <span className="text-sm text-[#5B6675] mb-2 block">Logo voorbeeld</span>
          <div className="flex items-center gap-6">
            <div className="h-20 w-48 rounded-xl bg-[#001C51] flex items-center justify-center overflow-hidden" data-testid="branding-preview-dark">
              {logo ? <img src={logo} alt="logo" className="max-h-16 max-w-[170px] object-contain" /> : <span className="text-white/60 text-sm">Nog geen logo</span>}
            </div>
            <div className="h-20 w-48 rounded-xl bg-white border border-[#E2E7EC] flex items-center justify-center overflow-hidden" data-testid="branding-preview-light">
              {logo ? <img src={logo} alt="logo" className="max-h-16 max-w-[170px] object-contain" /> : <span className="text-[#8994A3] text-sm">Op lichte achtergrond</span>}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label data-testid="branding-upload-label" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E7EC] text-[#001C51] font-medium hover:bg-[#EDF1F5] cursor-pointer">
            <Upload className="h-4 w-4" /> Logo kiezen
            <input data-testid="branding-file" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onFile} className="hidden" />
          </label>
          {logo && <button data-testid="branding-remove" onClick={() => setLogo("")} className="px-4 py-2.5 rounded-xl border border-[#E2E7EC] text-[#B0413E] font-medium hover:bg-[#F3DEDD]">Logo verwijderen</button>}
        </div>

        <label className="block">
          <span className="text-sm text-[#5B6675] mb-1.5 block">Bedrijfsnaam (fallback als er geen logo is)</span>
          <input data-testid="branding-company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="fe-input max-w-sm" />
        </label>

        <div className="pt-2 border-t border-[#E2E7EC]">
          <button data-testid="branding-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A] disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Opslaan…" : "Branding opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfigColumn({ title, items, onAdd, onSave, onDelete, testid }) {
  const [label, setLabel] = useState("");
  const [local, setLocal] = useState(items);
  useEffect(() => setLocal(items), [items]);
  return (
    <div className="flex-1 min-w-[280px]">
      <h3 className="font-heading font-semibold text-[#16202B] mb-3">{title}</h3>
      <div className="flex gap-2 mb-4">
        <input data-testid={`${testid}-new`} value={label} onChange={(e) => setLabel(e.target.value)} className="fe-input flex-1" placeholder="Nieuwe naam…" />
        <button data-testid={`${testid}-add`} onClick={() => { if (label.trim()) { onAdd(label.trim()); setLabel(""); } }} className="px-4 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]"><Plus className="h-4 w-4" /></button>
      </div>
      <div className="space-y-2">
        {local.map((it) => (
          <div key={it.id} data-testid={`${testid}-${it.id}`} className={`rounded-xl border border-[#E2E7EC] p-2.5 flex items-center gap-2 ${it.active ? "bg-white" : "bg-[#F6F9FB] opacity-70"}`}>
            <input value={it.label} onChange={(e) => setLocal((xs) => xs.map((x) => x.id === it.id ? { ...x, label: e.target.value } : x))} className="fe-input flex-1" />
            <button onClick={() => onSave(it)} className="p-2 rounded-lg text-[#001C51] hover:bg-[#E3E9F5]" title="Opslaan"><Save className="h-4 w-4" /></button>
            <button onClick={() => onDelete(it)} className="p-2 rounded-lg text-[#B0413E] hover:bg-[#F3DEDD]" title="Deactiveren"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PipelineConfigEditor() {
  const [pipeline, setPipeline] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const load = () => { getPipelineStatuses().then(setPipeline).catch(() => {}); getCallOutcomes().then(setOutcomes).catch(() => {}); };
  useEffect(load, []);

  return (
    <div>
      <h2 className="font-heading font-semibold text-xl text-[#16202B] mb-1">Pipeline & gespreksuitkomsten</h2>
      <p className="text-sm text-[#5B6675] mb-6">Beheer je eigen pipeline-statussen en belresultaten. Deactiveren verbergt een optie zonder bestaande leads te raken.</p>
      <div className="flex flex-wrap gap-10">
        <ConfigColumn title="Pipeline-statussen" items={pipeline} testid="pipeline"
          onAdd={async (label) => { await createPipelineStatus({ label }); load(); toast.success("Toegevoegd"); }}
          onSave={async (it) => { await updatePipelineStatus(it.id, { label: it.label, active: it.active }); toast.success("Opgeslagen"); load(); }}
          onDelete={async (it) => { await deletePipelineStatus(it.id); toast.success("Gedeactiveerd"); load(); }} />
        <ConfigColumn title="Gespreksuitkomsten" items={outcomes} testid="outcome"
          onAdd={async (label) => { await createCallOutcome({ label }); load(); toast.success("Toegevoegd"); }}
          onSave={async (it) => { await updateCallOutcome(it.id, { label: it.label, active: it.active }); toast.success("Opgeslagen"); load(); }}
          onDelete={async (it) => { await deleteCallOutcome(it.id); toast.success("Gedeactiveerd"); load(); }} />
      </div>
    </div>
  );
}

const SERVICE_FIELDS = [
  { k: "name", label: "Naam *", type: "input" },
  { k: "category", label: "Categorie", type: "input" },
  { k: "description", label: "Korte omschrijving", type: "textarea" },
  { k: "price", label: "Prijs", type: "input" },
  { k: "vat_status", label: "Btw-status", type: "input" },
  { k: "price_formula", label: "Prijsformule (optioneel)", type: "input" },
  { k: "included", label: "Wat is inbegrepen", type: "textarea" },
  { k: "excluded", label: "Wat is niet inbegrepen", type: "textarea" },
  { k: "duration", label: "Looptijd", type: "input" },
  { k: "conditions", label: "Voorwaarden & bijzonderheden", type: "textarea" },
  { k: "website_pages", label: "Relevante websitepagina's (volledige URL's)", type: "textarea" },
  { k: "checkout_link", label: "Directe bestel-/checkoutlink", type: "input" },
  { k: "internal_notes", label: "Interne instructies voor medewerkers", type: "textarea" },
];
const emptyService = { name: "", category: "", description: "", price: "", vat_status: "", price_formula: "", included: "", excluded: "", duration: "", conditions: "", website_pages: "", checkout_link: "", internal_notes: "", lead_categories: "", routes: "", status: "draft" };

function StatusSelect({ value, onChange }) {
  return (
    <select value={value} onChange={onChange} className="fe-input w-36">
      <option value="draft">Concept</option>
      <option value="published">Gepubliceerd</option>
      <option value="archived">Gearchiveerd</option>
    </select>
  );
}

export function ServiceEditor() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState(null);
  const load = () => getServices().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const edit = (it) => { setOpenId(it.id); setDraft({ ...emptyService, ...it }); };
  const addNew = () => { setOpenId("new"); setDraft({ ...emptyService }); };
  const setF = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Naam is verplicht");
    if (openId === "new") await createService(draft); else await updateService(openId, draft);
    toast.success("Dienst opgeslagen"); setOpenId(null); setDraft(null); load();
  };
  const remove = async (it) => { if (!window.confirm(`"${it.name}" verwijderen?`)) return; await deleteService(it.id); toast.success("Verwijderd"); load(); };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-semibold text-xl text-[#16202B] flex items-center gap-2"><Package className="h-5 w-5 text-[#16BFBF]" /> Diensten & pakketten</h2>
          <p className="text-sm text-[#5B6675] mt-1">Voeg je eigen pakketten, prijzen en voorwaarden toe. Alleen 'Gepubliceerd' is zichtbaar voor medewerkers.</p>
        </div>
        <button data-testid="add-service" onClick={addNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]"><Plus className="h-4 w-4" /> Nieuwe dienst</button>
      </div>

      {openId === "new" && draft && <ServiceForm draft={draft} setF={setF} setStatus={(e) => setDraft((d) => ({ ...d, status: e.target.value }))} onSave={save} onCancel={() => { setOpenId(null); setDraft(null); }} />}

      <div className="space-y-2 mt-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`svc-row-${it.id}`} className="rounded-xl border border-[#E2E7EC] bg-white">
            <div className="p-3 flex items-center gap-3">
              <button onClick={() => (openId === it.id ? setOpenId(null) : edit(it))} className="flex-1 text-left flex items-center gap-2">
                <ChevronDown className={`h-4 w-4 text-[#8994A3] transition-transform ${openId === it.id ? "rotate-180" : ""}`} />
                <span className="font-heading font-semibold text-[#16202B]">{it.name}</span>
                {it.category && <span className="text-xs text-[#5B6675]">· {it.category}</span>}
                {it.price && <span className="text-xs text-[#001C51] font-medium">· {it.price}</span>}
              </button>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${it.status === "published" ? "bg-[#D6F3F3] text-[#0C6E6E]" : it.status === "archived" ? "bg-[#EDF1F5] text-[#5B6675]" : "bg-[#FBF0DC] text-[#B7791F]"}`}>{it.status === "published" ? "Gepubliceerd" : it.status === "archived" ? "Gearchiveerd" : "Concept"}</span>
              <button data-testid={`del-svc-${it.id}`} onClick={() => remove(it)} className="p-2 rounded-lg text-[#B0413E] hover:bg-[#F3DEDD]"><Trash2 className="h-4 w-4" /></button>
            </div>
            {openId === it.id && draft && <div className="px-3 pb-3"><ServiceForm draft={draft} setF={setF} setStatus={(e) => setDraft((d) => ({ ...d, status: e.target.value }))} onSave={save} onCancel={() => { setOpenId(null); setDraft(null); }} /></div>}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#5B6675] py-3">Nog geen diensten. Voeg je eerste pakket toe.</p>}
      </div>
    </div>
  );
}

function ServiceForm({ draft, setF, setStatus, onSave, onCancel }) {
  const CATS = [["verkoper", "Verkoper"], ["verhuurder", "Verhuurder"], ["koper", "Koper"], ["huurder", "Huurder"], ["partner", "Partner"], ["overig", "Overig"]];
  const selected = (draft.lead_categories || "").split(",").map((s) => s.trim()).filter(Boolean);
  const toggleCat = (key) => {
    const next = selected.includes(key) ? selected.filter((x) => x !== key) : [...selected, key];
    setF("lead_categories")({ target: { value: next.join(",") } });
  };
  return (
    <div data-testid="service-form" className="rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4 space-y-3">
      {SERVICE_FIELDS.map((f) => (
        <label key={f.k} className="block">
          <span className="text-xs text-[#5B6675] block mb-1">{f.label}</span>
          {f.type === "textarea"
            ? <textarea data-testid={`svc-${f.k}`} value={draft[f.k] || ""} onChange={setF(f.k)} rows={2} className="fe-input resize-y" />
            : <input data-testid={`svc-${f.k}`} value={draft[f.k] || ""} onChange={setF(f.k)} className="fe-input" />}
        </label>
      ))}
      <div>
        <span className="text-xs text-[#5B6675] block mb-1.5">Past bij klantwens (voor de pakketvoorstellen in het gesprek)</span>
        <div className="flex flex-wrap gap-2" data-testid="svc-wish-tags">
          {[["zelf", "Zelf verkopen"], ["deels", "Deel van het proces"], ["volledig", "Volledige begeleiding"]].map(([key, label]) => {
            const tags = draft.wish_tags || [];
            const on = tags.includes(key);
            return (
              <button key={key} type="button" data-testid={`svc-wish-${key}`} onClick={() => setF("wish_tags")({ target: { value: on ? tags.filter((t) => t !== key) : [...tags, key] } })}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#16BFBF] text-white border-[#16BFBF]" : "bg-white text-[#5B6675] border-[#E2E7EC] hover:border-[#16BFBF]"}`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <span className="text-xs text-[#5B6675] block mb-1.5">Toon dit pakket bij lead-type(s) — aanbevelingsregel</span>
        <div className="flex flex-wrap gap-2" data-testid="svc-lead-categories">
          {CATS.map(([key, label]) => (
            <button key={key} type="button" onClick={() => toggleCat(key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${selected.includes(key) ? "bg-[#001C51] text-white border-[#001C51]" : "bg-white text-[#5B6675] border-[#E2E7EC] hover:border-[#001C51]"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="text-xs text-[#5B6675] block mb-1">Route/behoefte-labels (komma-gescheiden, optioneel) — bijv. zelf verkopen, met begeleiding, full service</span>
        <input data-testid="svc-routes" value={draft.routes || ""} onChange={setF("routes")} className="fe-input" />
      </label>
      <div className="flex items-center gap-3 pt-1">
        <div><span className="text-xs text-[#5B6675] block mb-1">Status</span><StatusSelect value={draft.status} onChange={setStatus} /></div>
        <button data-testid="save-service" onClick={onSave} className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]"><Save className="h-4 w-4" /> Opslaan</button>
        <button onClick={onCancel} className="mt-5 px-4 py-2.5 rounded-xl border border-[#E2E7EC] text-[#5B6675] font-medium hover:bg-[#EDF1F5]">Annuleren</button>
      </div>
    </div>
  );
}

const emptyKb = { title: "", category: "", body: "", tags: "", status: "draft" };

export function KbEditor() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState(null);
  const load = () => getKbArticles().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const edit = (it) => { setOpenId(it.id); setDraft({ ...emptyKb, ...it }); };
  const addNew = () => { setOpenId("new"); setDraft({ ...emptyKb }); };
  const setF = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const save = async () => {
    if (!draft.title.trim()) return toast.error("Titel is verplicht");
    if (openId === "new") await createKbArticle(draft); else await updateKbArticle(openId, draft);
    toast.success("Artikel opgeslagen"); setOpenId(null); setDraft(null); load();
  };
  const remove = async (it) => { if (!window.confirm(`"${it.title}" verwijderen?`)) return; await deleteKbArticle(it.id); toast.success("Verwijderd"); load(); };

  const Form = draft && (
    <div data-testid="kb-form" className="rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4 space-y-3">
      <label className="block"><span className="text-xs text-[#5B6675] block mb-1">Titel *</span><input data-testid="kb-title" value={draft.title} onChange={setF("title")} className="fe-input" /></label>
      <label className="block"><span className="text-xs text-[#5B6675] block mb-1">Categorie</span><input data-testid="kb-category" value={draft.category} onChange={setF("category")} className="fe-input" placeholder="Bijv. Werkwijze, FAQ, Bezwaren" /></label>
      <label className="block"><span className="text-xs text-[#5B6675] block mb-1">Inhoud</span><textarea data-testid="kb-body" value={draft.body} onChange={setF("body")} rows={6} className="fe-input resize-y" placeholder="Volledige URL's worden automatisch klikbaar." /></label>
      <label className="block"><span className="text-xs text-[#5B6675] block mb-1">Labels (komma-gescheiden)</span><input data-testid="kb-tags" value={draft.tags} onChange={setF("tags")} className="fe-input" placeholder="funda, prijs, checkout" /></label>
      <div className="flex items-center gap-3 pt-1">
        <div><span className="text-xs text-[#5B6675] block mb-1">Status</span><StatusSelect value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))} /></div>
        <button data-testid="save-kb" onClick={save} className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]"><Save className="h-4 w-4" /> Opslaan</button>
        <button onClick={() => { setOpenId(null); setDraft(null); }} className="mt-5 px-4 py-2.5 rounded-xl border border-[#E2E7EC] text-[#5B6675] font-medium hover:bg-[#EDF1F5]">Annuleren</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-semibold text-xl text-[#16202B] flex items-center gap-2"><BookOpen className="h-5 w-5 text-[#16BFBF]" /> Kennisbankartikelen</h2>
          <p className="text-sm text-[#5B6675] mt-1">Werkwijze, FAQ, bezwaarafhandeling en instructies. Alleen 'Gepubliceerd' is zichtbaar voor medewerkers.</p>
        </div>
        <button data-testid="add-kb" onClick={addNew} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]"><Plus className="h-4 w-4" /> Nieuw artikel</button>
      </div>
      {openId === "new" && Form}
      <div className="space-y-2 mt-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`kb-row-${it.id}`} className="rounded-xl border border-[#E2E7EC] bg-white">
            <div className="p-3 flex items-center gap-3">
              <button onClick={() => (openId === it.id ? setOpenId(null) : edit(it))} className="flex-1 text-left flex items-center gap-2">
                <ChevronDown className={`h-4 w-4 text-[#8994A3] transition-transform ${openId === it.id ? "rotate-180" : ""}`} />
                <span className="font-heading font-semibold text-[#16202B]">{it.title}</span>
                {it.category && <span className="text-xs text-[#5B6675]">· {it.category}</span>}
              </button>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${it.status === "published" ? "bg-[#D6F3F3] text-[#0C6E6E]" : it.status === "archived" ? "bg-[#EDF1F5] text-[#5B6675]" : "bg-[#FBF0DC] text-[#B7791F]"}`}>{it.status === "published" ? "Gepubliceerd" : it.status === "archived" ? "Gearchiveerd" : "Concept"}</span>
              <button data-testid={`del-kb-${it.id}`} onClick={() => remove(it)} className="p-2 rounded-lg text-[#B0413E] hover:bg-[#F3DEDD]"><Trash2 className="h-4 w-4" /></button>
            </div>
            {openId === it.id && <div className="px-3 pb-3">{Form}</div>}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#5B6675] py-3">Nog geen artikelen.</p>}
      </div>
    </div>
  );
}
